import { onAuthStateChanged } from 'firebase/auth'
import { auth } from '@/lib/firebase/app'

/** Set per environment in .env.development / .env.production. */
const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.voyp.app'

/**
 * Same host, websocket scheme — used by the live-call stream socket.
 * voyp-webapp derives this from Remote Config; this app has none, so it comes
 * straight off VITE_API_BASE.
 */
export const WS_BASE = API_BASE.replace(/^http/, 'ws')

export { API_BASE }

export class VoypApiError extends Error {
  status: number
  /** machine-readable reason, when the server sends one (e.g. `user_not_found`) */
  code?: string

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = 'VoypApiError'
    this.status = status
    this.code = code
  }

  /** The caller is signed in but is not an admin. */
  get isForbidden() {
    return this.status === 403
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
}

let authReady: Promise<void> | null = null

/**
 * Resolves once Firebase has restored (or ruled out) a persisted session.
 *
 * `auth.currentUser` is null for the first moments after page load even when
 * the user is signed in — the session is read back from IndexedDB
 * asynchronously. Reading it too early makes a reload look like a signed-out
 * user, so every request waits for the first onAuthStateChanged callback.
 */
function waitForAuth(): Promise<void> {
  if (!authReady) {
    authReady = new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, () => {
        unsubscribe()
        resolve()
      })
    })
  }
  return authReady
}

async function authHeader(): Promise<Record<string, string>> {
  await waitForAuth()
  const user = auth.currentUser
  if (!user) throw new VoypApiError('Not signed in', 401)
  const token = await user.getIdToken(false)
  return { Authorization: `Bearer ${token}` }
}

/**
 * Single choke point for all API calls.
 *
 * The server frequently answers HTTP 200 with an `{error}` body — every handler
 * in voyp-nodejs/src/admin.ts does `res.json({error: message})` in its catch —
 * so both transport and in-body errors are normalized into VoypApiError.
 * Without this, a failed request renders as an empty table instead of an error.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = await authHeader()
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'

  const res = await fetch(API_BASE + path, {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  })

  if (!res.ok) {
    let message = res.status === 403 ? 'Not authorized' : `Request failed (${res.status})`
    let code: string | undefined
    try {
      const body = await res.json()
      if (body?.error) message = String(body.error)
      if (typeof body?.code === 'string') code = body.code
    } catch {
      // Non-JSON error body (the admin routes use res.sendStatus for auth
      // failures, which sends plain text); keep the status message.
    }
    throw new VoypApiError(message, res.status, code)
  }

  const data = (await res.json()) as T & { error?: string; code?: string }
  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new VoypApiError(String(data.error), res.status, typeof data.code === 'string' ? data.code : undefined)
  }
  return data
}

/** Text response with auth (e.g. the mailing-list CSV export). */
export async function apiFetchText(path: string): Promise<string> {
  const headers = await authHeader()
  const res = await fetch(API_BASE + path, { headers })
  if (!res.ok) throw new VoypApiError(`Request failed (${res.status})`, res.status)
  return res.text()
}

/** Query string from a filter object, dropping empty values. */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    search.set(key, String(value))
  }
  const str = search.toString()
  return str ? `?${str}` : ''
}
